import { useOnlineBookingState } from 'hooks/useOnlineBookingState';
import React from 'react';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { OnlineBookingBoardingAddonListStep } from 'components/OnlineBooking/Boarding/AddonList/OnlineBookingBoardingAddonListStep';
import { OnlineBookingBoardingDatePickerStep } from 'components/OnlineBooking/Boarding/DatePicker/OnlineBookingBoardingDatePickerStep';
import { OnlineBookingBoardingInstructionsStep } from 'components/OnlineBooking/Boarding/Instructions/OnlineBookingBoardingInstructions';
import { OnlineBookingBoardingServiceListStep } from 'components/OnlineBooking/Boarding/ServiceList/OnlineBookingBoardingServiceListStep';
import { OnlineBookingBoardingTimePicker } from 'components/OnlineBooking/Boarding/TimePicker/OnlineBookingBoardingTimePicker';
import { OnlineBookingDaycareAddonListStep } from 'components/OnlineBooking/Daycare/AddonList/OnlineBookingDaycareAddonListStep';
import { OnlineBookingDaycareDatePicker } from 'components/OnlineBooking/Daycare/DatePicker/OnlineBookingDaycareDatePicker';
import { OnlineBookingDaycareInstructionsStep } from 'components/OnlineBooking/Daycare/Instructions/OnlineBookingDaycareInstructionsStep';
import { OnlineBookingDaycareServiceListStep } from 'components/OnlineBooking/Daycare/ServiceList/OnlineBookingDaycareServiceListStep';
import { OnlineBookingDaycareTimePicker } from 'components/OnlineBooking/Daycare/TimePicker/OnlineBookingDaycareTimePicker';
import { OnlineBookingEvaluationDatePicker } from 'components/OnlineBooking/Evaluation/DatePicker/OnlineBookingEvaluationDatePicker';
import { OnlineBookingEvaluationServiceListStep } from 'components/OnlineBooking/Evaluation/ServiceList/OnlineBookingEvaluationServiceListStep';
import { OnlineBookingGroomingDatePicker } from 'components/OnlineBooking/Grooming/DatePicker/OnlineBookingGroomingDatePicker';
import { OnlineBookingGroomingServiceListStep } from 'components/OnlineBooking/Grooming/ServiceList/OnlineBookingGroomingServiceListStep';
import { OnlineBookingGroomingStaffListStep } from 'components/OnlineBooking/Grooming/StaffList/OnlineBookingGroomingStaffListStep';
import { OnlineBookingPurchaseCheckoutStep } from 'components/OnlineBooking/Purchase/Checkout/OnlineBookingPurchaseCheckoutStep';
import { OnlineBookingPurchaseStep } from 'components/OnlineBooking/Purchase/OfferList/OnlineBookingPurchaseStep';
import { OnlineBookingPurchaseSuccessStep } from 'components/OnlineBooking/Purchase/Success/OnlineBookingPurchaseSuccessStep';
import { OnlineBookingCreatePetStep } from 'components/OnlineBooking/Steps/AddPet/OnlineBookingCreatePetStep';
import { OnlineBookingAgreementsStep } from 'components/OnlineBooking/Steps/Agreements/OnlineBookingAgreementsStep';
import { OnlineBookingCheckoutStep } from 'components/OnlineBooking/Steps/Checkout/OnlineBookingCheckoutStep';
import { OnlineBookingJoinWaitlistStep } from 'components/OnlineBooking/Steps/JoinWaitlist/OnlineBookingJoinWaitlistStep';
import { OnlineBookingHomeStep } from 'components/OnlineBooking/Steps/Home/OnlineBookingHomeStep';
import { OnlineBookingPetListStep } from 'components/OnlineBooking/Steps/PetList/OnlineBookingPetListStep';
import { OnlineBookingServiceCategoryStep } from 'components/OnlineBooking/Steps/ServiceCategory/OnlineBookingServiceCategoryStep';
import { OnlineBookingSigninStep } from 'components/OnlineBooking/Steps/Signin/OnlineBookingSigninStep';
import { OnlineBookingSignupStep } from 'components/OnlineBooking/Steps/Signup/OnlineBookingSignupStep';
import { OnlineBookingSuccessStep } from 'components/OnlineBooking/Steps/Success/OnlineBookingSuccessStep';
import { OnlineBookingWalkingDatePicker } from 'components/OnlineBooking/Walking/DatePicker/OnlineBookingWalkingDatePicker';
import { OnlineBookingWalkingServiceListStep } from 'components/OnlineBooking/Walking/ServiceList/OnlineBookingWalkingServiceListStep';
import { OnlineBookingWalkingStaffListStep } from 'components/OnlineBooking/Walking/StaffList/OnlineBookingWalkingStaffListStep';
import { OnlineBookingWaitlistSuccessStep } from 'components/OnlineBooking/Steps/WaitlistSuccess/OnlineBookingWaitlistSuccessStep';
import { OnlineBookingStep, SessionBusinessArea, SessionStepProps } from 'types/OnlineBooking';

const DEFAULT_COMPONENTS_MAP: Map<OnlineBookingStep, React.FC> = new Map([
    [OnlineBookingStep.Home, OnlineBookingHomeStep],
    [OnlineBookingStep.Signup, OnlineBookingSignupStep],
    [OnlineBookingStep.Signin, OnlineBookingSigninStep],
    [OnlineBookingStep.CreatePet, OnlineBookingCreatePetStep],
    [OnlineBookingStep.PetList, OnlineBookingPetListStep],
    [OnlineBookingStep.ServiceCategory, OnlineBookingServiceCategoryStep],
    [OnlineBookingStep.Agreements, OnlineBookingAgreementsStep],
    [OnlineBookingStep.Checkout, OnlineBookingCheckoutStep],
    [OnlineBookingStep.Success, OnlineBookingSuccessStep],
    [OnlineBookingStep.WaitlistSuccess, OnlineBookingWaitlistSuccessStep],
    [OnlineBookingStep.Purchase, OnlineBookingPurchaseStep],
    [OnlineBookingStep.PurchaseCheckout, OnlineBookingPurchaseCheckoutStep],
    [OnlineBookingStep.PurchaseSuccess, OnlineBookingPurchaseSuccessStep]
]);

const GROOMING_COMPONENTS_MAP: Map<OnlineBookingStep, React.FC> = new Map([
    [OnlineBookingStep.ServiceList, OnlineBookingGroomingServiceListStep],
    [OnlineBookingStep.StaffList, OnlineBookingGroomingStaffListStep],
    [OnlineBookingStep.DatePicker, OnlineBookingGroomingDatePicker],
    [OnlineBookingStep.JoinWaitlist, OnlineBookingJoinWaitlistStep]
]);

const DAYCARE_COMPONENTS_MAP: Map<OnlineBookingStep, React.FC> = new Map([
    [OnlineBookingStep.ServiceList, OnlineBookingDaycareServiceListStep],
    [OnlineBookingStep.DatePicker, OnlineBookingDaycareDatePicker],
    [OnlineBookingStep.TimePicker, OnlineBookingDaycareTimePicker],
    [OnlineBookingStep.AddonList, OnlineBookingDaycareAddonListStep],
    [OnlineBookingStep.Instructions, OnlineBookingDaycareInstructionsStep],
    [OnlineBookingStep.JoinWaitlist, OnlineBookingJoinWaitlistStep]
]);

const BOARDING_COMPONENTS_MAP: Map<OnlineBookingStep, React.FC> = new Map([
    [OnlineBookingStep.DatePicker, OnlineBookingBoardingDatePickerStep],
    [OnlineBookingStep.ServiceList, OnlineBookingBoardingServiceListStep],
    [OnlineBookingStep.TimePicker, OnlineBookingBoardingTimePicker],
    [OnlineBookingStep.AddonList, OnlineBookingBoardingAddonListStep],
    [OnlineBookingStep.Instructions, OnlineBookingBoardingInstructionsStep],
    [OnlineBookingStep.JoinWaitlist, OnlineBookingJoinWaitlistStep]
]);

const EVALUATION_COMPONENTS_MAP: Map<OnlineBookingStep, React.FC> = new Map([
    [OnlineBookingStep.ServiceList, OnlineBookingEvaluationServiceListStep],
    [OnlineBookingStep.DatePicker, OnlineBookingEvaluationDatePicker]
]);

const withBusinessArea = (
    Step: React.FC<SessionStepProps>,
    businessArea: SessionBusinessArea
): React.FC => {
    const SessionStep: React.FC = () => <Step businessArea={businessArea} />;

    SessionStep.displayName = `${Step.displayName}(${businessArea})`;

    return SessionStep;
};

const createSessionComponentsMap = (
    businessArea: SessionBusinessArea
): Map<OnlineBookingStep, React.FC> =>
    new Map([
        [
            OnlineBookingStep.ServiceList,
            withBusinessArea(OnlineBookingWalkingServiceListStep, businessArea)
        ],
        [
            OnlineBookingStep.StaffList,
            withBusinessArea(OnlineBookingWalkingStaffListStep, businessArea)
        ],
        [
            OnlineBookingStep.DatePicker,
            withBusinessArea(OnlineBookingWalkingDatePicker, businessArea)
        ]
    ]);

const WALKING_COMPONENTS_MAP = createSessionComponentsMap(MarketplaceServiceCategory.WALKING);

const TRAINING_COMPONENTS_MAP = createSessionComponentsMap(MarketplaceServiceCategory.TRAINING);

export const OnlineBookingWizard: React.FC = React.memo(() => {
    const { currentStep, currentCategory } = useOnlineBookingState();

    const CurrentStepComponent = React.useMemo(() => {
        if (
            currentCategory === MarketplaceServiceCategory.EVALUATION &&
            EVALUATION_COMPONENTS_MAP.get(currentStep)
        ) {
            return EVALUATION_COMPONENTS_MAP.get(currentStep);
        }

        if (
            currentCategory === MarketplaceServiceCategory.GROOMING &&
            GROOMING_COMPONENTS_MAP.get(currentStep)
        ) {
            return GROOMING_COMPONENTS_MAP.get(currentStep);
        }

        if (
            currentCategory === MarketplaceServiceCategory.DAYCARE &&
            DAYCARE_COMPONENTS_MAP.get(currentStep)
        ) {
            return DAYCARE_COMPONENTS_MAP.get(currentStep);
        }

        if (
            currentCategory === MarketplaceServiceCategory.BOARDING &&
            BOARDING_COMPONENTS_MAP.get(currentStep)
        ) {
            return BOARDING_COMPONENTS_MAP.get(currentStep);
        }

        if (
            currentCategory === MarketplaceServiceCategory.WALKING &&
            WALKING_COMPONENTS_MAP.get(currentStep)
        ) {
            return WALKING_COMPONENTS_MAP.get(currentStep);
        }

        if (
            currentCategory === MarketplaceServiceCategory.TRAINING &&
            TRAINING_COMPONENTS_MAP.get(currentStep)
        ) {
            return TRAINING_COMPONENTS_MAP.get(currentStep);
        }

        return DEFAULT_COMPONENTS_MAP.get(currentStep);
    }, [currentCategory, currentStep]);

    return CurrentStepComponent ? <CurrentStepComponent /> : null;
});

OnlineBookingWizard.displayName = 'OnlineBookingWizard';
